/**
 * Adapted from PlayCanvas Engine's khr-gaussian-splatting.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import { type PlyProperty, SplatData } from '@/plugins/gsplat/core/SplatData'
import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import type {
  GltfExtensionAccessorData,
  GltfExtensionDiagnostic,
  GltfExtensionHook,
  GltfExtensionHookContext,
  GltfExtensionPrimitive,
  GltfExtensionResult,
} from '@/runtime/GltfExtensionRegistry'

export const KHR_GAUSSIAN_SPLATTING = 'KHR_gaussian_splatting'

const SH_DEGREE_COEFFICIENT_COUNTS = [3, 5, 7] as const
const SH_BAND_COEFFICIENT_COUNTS = { 1: 3, 2: 8, 3: 15 } as const

/** GSplatSystem 提供给 glTF hook 的跨 backend Resource factory 边界。 */
export interface GSplatGltfResourceFactory {
  assertResourceCount(numSplats: number): void
  createResource(data: SplatData): GSplatResourceLease
}

function hasOwnExtension(primitive: GltfExtensionPrimitive): boolean {
  return primitive.extensions?.[KHR_GAUSSIAN_SPLATTING] !== undefined
}

function component(data: GltfExtensionAccessorData, componentIndex: number): Float32Array {
  const result = new Float32Array(data.count)
  for (let i = 0; i < data.count; i += 1) {
    result[i] = data.data[i * data.componentCount + componentIndex]
  }
  return result
}

function disposeLeases(leases: readonly GSplatResourceLease[]): void {
  for (const lease of [...leases].reverse()) {
    try {
      lease.dispose()
    } catch {
      // two-phase rollback 必须继续释放剩余 lease，并由调用方保留原始错误。
    }
  }
}

/** 创建冻结 KHR_gaussian_splatting extension hook。 */
export function createKHRGaussianSplattingHook(
  resourceFactory: GSplatGltfResourceFactory
): GltfExtensionHook {
  return {
    extensionName: KHR_GAUSSIAN_SPLATTING,
    hasPrimitive: hasOwnExtension,
    async createResources(context): Promise<GltfExtensionResult> {
      const diagnostics: GltfExtensionDiagnostic[] = []
      const diagnosticCodes = new Set<string>()
      const staged: GSplatResourceLease[] = []
      const addDiagnostic = (
        level: GltfExtensionDiagnostic['level'],
        code: string,
        message: string
      ) => {
        if (diagnosticCodes.has(code)) return
        diagnosticCodes.add(code)
        diagnostics.push(Object.freeze({ level, code, message }))
      }

      if (!context.document.meshes) {
        return createResult([], diagnostics, staged, 0)
      }

      const resourcesByMesh: (GSplatResourceLease[] | null)[] = []
      let handledPrimitiveCount = 0
      try {
        for (const mesh of context.document.meshes) {
          let resources: GSplatResourceLease[] | null = null
          for (const primitive of mesh.primitives) {
            if (!hasOwnExtension(primitive)) continue
            handledPrimitiveCount += 1
            reportHints(primitive, addDiagnostic)
            const data = await createGSplatData(primitive, context, resourceFactory, addDiagnostic)
            if (!data) continue

            data.reorderData()
            const resource = resourceFactory.createResource(data)
            staged.push(resource)
            resources ??= []
            resources.push(resource)
          }
          resourcesByMesh.push(resources)
        }
        return createResult(resourcesByMesh, diagnostics, staged, handledPrimitiveCount)
      } catch (error) {
        disposeLeases(staged)
        throw error
      }
    },
  }
}

function createResult(
  resourcesByMesh: readonly (readonly GSplatResourceLease[] | null)[],
  diagnostics: readonly GltfExtensionDiagnostic[],
  leases: readonly GSplatResourceLease[],
  handledPrimitiveCount: number
): GltfExtensionResult {
  let disposed = false
  return {
    extensionName: KHR_GAUSSIAN_SPLATTING,
    handledPrimitiveCount,
    resourcesByMesh: Object.freeze(
      resourcesByMesh.map((resources) => (resources ? Object.freeze([...resources]) : null))
    ),
    diagnostics: Object.freeze([...diagnostics]),
    dispose: () => {
      if (disposed) return
      disposed = true
      disposeLeases(leases)
    },
  }
}

function reportHints(
  primitive: GltfExtensionPrimitive,
  diagnostic: (level: 'warning' | 'error', code: string, message: string) => void
): void {
  const extension = primitive.extensions?.[KHR_GAUSSIAN_SPLATTING] as
    | Record<string, unknown>
    | undefined
  if (primitive.mode !== 0) {
    diagnostic(
      'warning',
      'non-points-mode',
      `${KHR_GAUSSIAN_SPLATTING} 位于非 POINTS primitive，按 PlayCanvas fallback 继续`
    )
  }
  if (extension?.kernel !== undefined && extension.kernel !== 'ellipse') {
    diagnostic(
      'warning',
      'unsupported-kernel',
      `${KHR_GAUSSIAN_SPLATTING} kernel=${String(extension.kernel)} 不支持，按 ellipse 渲染`
    )
  }
  if (extension?.colorSpace === 'lin_rec709_display') {
    diagnostic(
      'warning',
      'unsupported-color-space',
      `${KHR_GAUSSIAN_SPLATTING} lin_rec709_display 暂按 srgb_rec709_display 处理`
    )
  }
}

async function createGSplatData(
  primitive: GltfExtensionPrimitive,
  context: GltfExtensionHookContext,
  resourceFactory: GSplatGltfResourceFactory,
  diagnostic: (level: 'warning' | 'error', code: string, message: string) => void
): Promise<SplatData | null> {
  const attributes = primitive.attributes
  const positionIndex = attributes.POSITION
  const positionAccessor = context.document.accessors?.[positionIndex]
  const numSplats = positionAccessor?.count ?? 0

  if (!Number.isSafeInteger(numSplats) || numSplats <= 0) {
    diagnostic(
      'error',
      'invalid-required-attributes',
      `${KHR_GAUSSIAN_SPLATTING} 缺少有效 required attributes，primitive 已跳过`
    )
    return null
  }

  // 可信 POSITION count 后、读取其余大数组与任何 GPU 分配前执行硬上限门禁。
  resourceFactory.assertResourceCount(numSplats)

  const readAttribute = async (
    name: string,
    componentCount: number
  ): Promise<GltfExtensionAccessorData | null> => {
    const accessorIndex = attributes[name]
    const accessor = context.document.accessors?.[accessorIndex]
    if (accessorIndex === undefined || !accessor || accessor.count !== numSplats) return null
    try {
      const data = await context.accessorReader.read(accessorIndex, context.signal)
      if (
        data.count !== numSplats ||
        data.componentCount !== componentCount ||
        data.data.length !== numSplats * componentCount
      ) {
        return null
      }
      return data
    } catch (error) {
      if (context.signal?.aborted || (error instanceof Error && error.name === 'AbortError')) {
        throw error
      }
      return null
    }
  }

  const [positions, rotations, scales, opacities, sh0] = await Promise.all([
    readAttribute('POSITION', 3),
    readAttribute(`${KHR_GAUSSIAN_SPLATTING}:ROTATION`, 4),
    readAttribute(`${KHR_GAUSSIAN_SPLATTING}:SCALE`, 3),
    readAttribute(`${KHR_GAUSSIAN_SPLATTING}:OPACITY`, 1),
    readAttribute(`${KHR_GAUSSIAN_SPLATTING}:SH_DEGREE_0_COEF_0`, 3),
  ])

  if (!positions || !rotations || !scales || !opacities || !sh0) {
    diagnostic(
      'error',
      'invalid-required-attributes',
      `${KHR_GAUSSIAN_SPLATTING} 缺少有效 required attributes，primitive 已跳过`
    )
    return null
  }

  const properties: PlyProperty[] = []
  const add = (name: string, storage: Float32Array) => {
    properties.push({ type: 'float', name, storage })
  }
  add('x', component(positions, 0))
  add('y', component(positions, 1))
  add('z', component(positions, 2))
  // glTF xyzw -> PLY rot_0=w, rot_1..3=xyz
  add('rot_0', component(rotations, 3))
  add('rot_1', component(rotations, 0))
  add('rot_2', component(rotations, 1))
  add('rot_3', component(rotations, 2))
  add('scale_0', component(scales, 0))
  add('scale_1', component(scales, 1))
  add('scale_2', component(scales, 2))
  add('opacity', component(opacities, 0))
  add('f_dc_0', component(sh0, 0))
  add('f_dc_1', component(sh0, 1))
  add('f_dc_2', component(sh0, 2))

  let bands: 0 | 1 | 2 | 3 = 0
  for (let degree = 1; degree <= 3; degree += 1) {
    let complete = true
    for (
      let coefficient = 0;
      coefficient < SH_DEGREE_COEFFICIENT_COUNTS[degree - 1];
      coefficient += 1
    ) {
      const name = `${KHR_GAUSSIAN_SPLATTING}:SH_DEGREE_${degree}_COEF_${coefficient}`
      if (attributes[name] === undefined) {
        complete = false
        break
      }
    }
    if (!complete) break
    bands = degree as 1 | 2 | 3
  }

  if (bands > 0) {
    const coefficientCount = SH_BAND_COEFFICIENT_COUNTS[bands]
    const rest = new Array<Float32Array>(coefficientCount * 3)
    let targetIndex = 0
    for (let degree = 1; degree <= bands; degree += 1) {
      for (
        let coefficient = 0;
        coefficient < SH_DEGREE_COEFFICIENT_COUNTS[degree - 1];
        coefficient += 1
      ) {
        const name = `${KHR_GAUSSIAN_SPLATTING}:SH_DEGREE_${degree}_COEF_${coefficient}`
        const source = await readAttribute(name, 3)
        if (!source) {
          diagnostic(
            'error',
            'invalid-sh-attribute',
            `${KHR_GAUSSIAN_SPLATTING} 高阶 SH accessor 无效，primitive 已跳过`
          )
          return null
        }
        rest[targetIndex] = component(source, 0)
        rest[coefficientCount + targetIndex] = component(source, 1)
        rest[coefficientCount * 2 + targetIndex] = component(source, 2)
        targetIndex += 1
      }
    }
    rest.forEach((storage, index) => add(`f_rest_${index}`, storage))
  }

  const data = new SplatData([{ name: 'vertex', count: numSplats, properties }])
  data.activated = true
  return data
}
